import { momentDate } from '../dates';
import { useEffect, useState, type FormEvent } from 'react';
import { graphql } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/Workspace';

type Code = { id: string; code: string; noticeCredits: number; maximumRedemptions: number; redemptions: number; eligibleCompany: string | null;
  startsAt: string; endsAt: string | null; enabled: boolean; reason: string; createdBy: string | null; createdAt: string };
type Company = { id: string; name: string };
const day = momentDate;
const state = (code: Code) => !code.enabled ? 'Turned off' : code.endsAt && Date.parse(code.endsAt) <= Date.now() ? 'Ended'
  : code.redemptions >= code.maximumRedemptions ? 'Used up' : 'Can be used';

/** Staff give notice credits with a code the company enters on Plan & billing. Each credit covers one notice. */
export function StaffPromotionsPage() {
  const { token } = useAuth();
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Code | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!token) return;
    graphql<{ promotionCodes: Code[]; staffCompanies: Company[] }>({ query: 'query Promotions { promotionCodes { id code noticeCredits maximumRedemptions redemptions eligibleCompany startsAt endsAt enabled reason createdBy createdAt } staffCompanies { id name } }', token })
      .then(data => { setCodes(data.promotionCodes); setCompanies(data.staffCompanies); setError(''); })
      .catch((reason: Error) => setError(reason.message));
  }, [token, revision]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); if (!token) return;
    const form = event.currentTarget; const values = new FormData(form);
    const until = String(values.get('endsOn') || '');
    const input = { code: String(values.get('code') || '').trim() || undefined, noticeCredits: Number(values.get('credits')), maximumRedemptions: Number(values.get('uses')),
      companyId: String(values.get('company') || '') || null, endsAt: until ? new Date(`${until}T23:59:59`).toISOString() : null, reason: String(values.get('reason')).trim() };
    setBusy(true); setError('');
    try {
      const result = await graphql<{ createPromotionCode: Code }>({ query: 'mutation Create($input:JSON!){createPromotionCode(input:$input){id code noticeCredits maximumRedemptions redemptions eligibleCompany startsAt endsAt enabled reason createdBy createdAt}}', variables: { input }, token });
      setCreated(result.createPromotionCode); form.reset(); setRevision(value => value + 1);
    } catch (reason) { setError((reason as Error).message); } finally { setBusy(false); }
  }
  async function toggle(code: Code) {
    if (!token) return;
    setBusy(true); setError('');
    try { await graphql({ query: 'mutation Toggle($id:ID!,$enabled:Boolean!){setPromotionCodeEnabled(id:$id,enabled:$enabled){id}}', variables: { id: code.id, enabled: !code.enabled }, token }); setRevision(value => value + 1); }
    catch (reason) { setError((reason as Error).message); } finally { setBusy(false); }
  }
  return <>
    <PageHeader eyebrow="Promotions" title="Promotion codes" />
    <p className="workspace-intro">A code gives a company notice credits; each credit covers one notice’s charge. The company enters the code on its Plan &amp; billing page. Credits already given stay when a code is turned off.</p>
    {error && <div className="alert error" role="alert">{error}</div>}
    {created && <div className="alert" role="status">Created <strong>{created.code}</strong>: {created.noticeCredits} {created.noticeCredits === 1 ? 'credit' : 'credits'}{created.eligibleCompany ? ` for ${created.eligibleCompany}` : ''}. Give the company this code to enter on Plan &amp; billing.</div>}
    <section className="workspace-section" aria-label="Create a code">
      <div className="section-heading"><div><h2>Create a code</h2></div></div>
      <form className="stack settings-form" onSubmit={event => void create(event)}>
        <label>Credits it gives<input name="credits" type="number" min={1} max={100} required defaultValue={1} /></label>
        <label>Companies that can use it<input name="uses" type="number" min={1} max={1000} required defaultValue={1} /></label>
        <label>Only for this company (optional)<select name="company" defaultValue=""><option value="">Any company</option>{companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label>
        <label>Ends on (optional)<input name="endsOn" type="date" /></label>
        <label>Code (optional)<input name="code" placeholder="Leave empty for a suggested code" maxLength={40} /></label>
        <label>Why<input name="reason" required minLength={10} maxLength={1000} placeholder="For example: replacement notices for Eval 1" /></label>
        <button className="button" disabled={busy}>{busy ? 'Creating…' : 'Create code'}</button>
      </form>
    </section>
    <section className="workspace-section" aria-label="Codes">
      <div className="section-heading"><div><h2>Codes</h2></div></div>
      {!codes ? <p className="muted">Loading codes…</p> : !codes.length ? <p className="empty-note">No codes yet.</p> : <div className="data-table"><table><thead><tr>
        <th>Code</th><th>Gives</th><th>Used</th><th>For</th><th>Status</th><th>Why</th><th></th></tr></thead><tbody>{codes.map(code => <tr key={code.id}>
          <td><strong>{code.code}</strong><small>{day(code.createdAt)}{code.createdBy ? `, by ${code.createdBy}` : ''}</small></td>
          <td>{code.noticeCredits} {code.noticeCredits === 1 ? 'credit' : 'credits'}</td>
          <td>{code.redemptions} of {code.maximumRedemptions}</td>
          <td>{code.eligibleCompany ?? 'Any company'}{code.endsAt && <small>Until {day(code.endsAt)}</small>}</td>
          <td>{state(code)}</td>
          <td className="promotion-reason">{code.reason}</td>
          <td><button type="button" className="link-button" disabled={busy} onClick={() => void toggle(code)}>{code.enabled ? 'Turn off' : 'Turn on'}</button></td>
        </tr>)}</tbody></table></div>}
    </section>
  </>;
}
